import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, LogOut, AlertTriangle, Calendar, ShieldCheck, Truck, Clock, Package,
  Edit3, Save, X, FileText, BarChart3, Check, Ban, PackageCheck, Printer,
  Wallet, Upload, Trash2, LayoutGrid, ShoppingBag, Megaphone, MessageSquare,
  User, TrendingUp, TrendingDown, ChevronRight, Search, MoreHorizontal,
} from 'lucide-react';

import VendorSidebar from '../components/vendor-portal/VendorSidebar';
import VendorPageHeader from '../components/vendor-portal/VendorPageHeader';
import KpiCard from '../components/vendor-portal/KpiCard';
import StatusPill from '../components/vendor-portal/StatusPill';
import DataTableShell from '../components/vendor-portal/DataTableShell';
import DrawerDetail from '../components/vendor-portal/DrawerDetail';
import EmptyState from '../components/vendor-portal/EmptyState';
import '../styles/vendor-portal.css';
import { DateInput } from '@/components/ui/date-input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TOKEN_KEY = 'marketplace_vendor_token';

const CATS = [
  { value: 'payment', label: 'Payment' },
  { value: 'po_issue', label: 'PO Issue' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'other', label: 'Other' },
];

const fmtMoney = (n, cur = 'AED') => {
  if (n == null) return '—';
  const num = typeof n === 'number' ? n : parseFloat(n);
  if (Number.isNaN(num)) return '—';
  return `${cur} ${num.toFixed(2)}`;
};

// ─────────────────────────────────────────────────────────────────────────────
//                      Main Vendor Portal Page
// ─────────────────────────────────────────────────────────────────────────────

const MarketplaceVendorDashboardPage = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState(null);
  const [editingSku, setEditingSku] = useState(null);
  const [editValues, setEditValues] = useState({ cost_price: '', quantity: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [activeNav, setActiveNav] = useState('dashboard');
  const [pos, setPos] = useState(null);
  const [perf, setPerf] = useState(null);
  const [eligiblePos, setEligiblePos] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [selectedPayoutPos, setSelectedPayoutPos] = useState(new Set());
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [creatingPayout, setCreatingPayout] = useState(false);
  const [announcements, setAnnouncements] = useState({ rows: [], unread_count: 0 });
  const [tickets, setTickets] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [newTicketOpen, setNewTicketOpen] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

  // ── Data loaders ──────────────────────────────────────────────────────────
  const loadAnnouncements = () =>
    axios.get(`${API}/marketplace-vendor/announcements`, { headers: authHeaders() })
      .then((r) => setAnnouncements(r.data)).catch(() => {});

  const markAnnouncementRead = (id) =>
    axios.post(`${API}/marketplace-vendor/announcements/${id}/read`, {}, { headers: authHeaders() })
      .then(loadAnnouncements).catch(() => {});

  const loadTickets = () =>
    axios.get(`${API}/marketplace-vendor/tickets`, { headers: authHeaders() })
      .then((r) => setTickets(r.data)).catch(() => {});

  const openTicket = (id) =>
    axios.get(`${API}/marketplace-vendor/tickets/${id}`, { headers: authHeaders() })
      .then((r) => setTicketDetail(r.data))
      .catch(() => toast.error('Failed to open ticket'));

  const loadPayoutEligible = () => {
    Promise.all([
      axios.get(`${API}/marketplace-vendor/payout/eligible-pos`, { headers: authHeaders() }),
      axios.get(`${API}/marketplace-vendor/payouts`, { headers: authHeaders() }),
    ]).then(([er, pr]) => { setEligiblePos(er.data); setPayouts(pr.data); }).catch(() => {});
  };

  const loadPOs = () =>
    axios.get(`${API}/marketplace-vendor/pos`, { headers: authHeaders() })
      .then((r) => setPos(r.data)).catch(() => {});

  const loadPerf = () =>
    axios.get(`${API}/marketplace-vendor/performance/me`, { headers: authHeaders() })
      .then((r) => setPerf(r.data)).catch(() => {});

  const loadInventory = () =>
    axios.get(`${API}/marketplace-vendor/inventory`, { headers: authHeaders() })
      .then((r) => setInventory(r.data)).catch(() => {});

  // ── Inventory edits ───────────────────────────────────────────────────────
  // Edit mode is now "cost" or "quantity" (or null) — separate buttons
  // so the vendor can edit either field independently. (Feb 2026 fix.)
  const beginEdit = (row, field) => {
    setEditingSku(`${row.sku}::${field}`);
    setEditValues({ cost_price: row.cost_price ?? '', quantity: row.quantity ?? '' });
  };
  const cancelEdit = () => { setEditingSku(null); setEditValues({ cost_price: '', quantity: '' }); };
  const saveEdit = async (row, field) => {
    const body = {};
    if (field === 'cost') {
      const newCost = parseFloat(editValues.cost_price);
      if (!Number.isNaN(newCost) && newCost !== row.cost_price) body.cost_price = newCost;
    } else if (field === 'quantity' && inventory?.allow_edit_quantity) {
      const newQty = parseInt(editValues.quantity, 10);
      if (!Number.isNaN(newQty) && newQty !== row.quantity) body.quantity = newQty;
    }
    if (Object.keys(body).length === 0) { cancelEdit(); return; }
    setSavingEdit(true);
    try {
      const r = await axios.post(`${API}/marketplace-vendor/inventory/${row.sku}/update`, body, { headers: authHeaders() });
      toast.success(r.data.selling_price ? `Saved · selling price ${r.data.selling_price}` : 'Saved');
      cancelEdit();
      await loadInventory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingEdit(false); }
  };

  // ── PO actions ────────────────────────────────────────────────────────────
  const poAction = async (poId, action, body) => {
    try {
      await axios.post(`${API}/marketplace-vendor/pos/${poId}/${action}`, body || {}, { headers: authHeaders() });
      toast.success(action.replace(/-/g, ' '));
      await loadPOs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const openPickupTag = (poId) => {
    fetch(`${API}/marketplace-vendor/pos/${poId}/pickup-tag`, { headers: authHeaders() })
      .then((r) => r.ok ? r.text() : Promise.reject(r))
      .then((html) => {
        const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
        const win = window.open(url, '_blank');
        if (!win) toast.error('Popup blocked — allow popups to print the tag');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(() => toast.error('Could not open pickup tag'));
  };

  const downloadShippingLabel = (poId) => {
    fetch(`${API}/marketplace-vendor/pos/${poId}/shipping-label`, { headers: authHeaders() })
      .then(async (r) => {
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.detail || 'Label not available');
        }
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch((e) => toast.error(e.message || 'Could not download shipping label'));
  };

  const bulkAction = async (poIds, action) => {
    try {
      const r = await axios.post(`${API}/marketplace-vendor/pos/${action}`,
        { po_ids: poIds }, { headers: authHeaders() });
      const ok = r.data.success_count || 0;
      const failed = (r.data.failed || []).length;
      toast.success(`${ok} / ${poIds.length} updated${failed ? ` · ${failed} failed` : ''}`);
      await loadPOs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk action failed'); }
  };

  const bulkPrint = (poIds) => {
    const ids = poIds.join(',');
    fetch(`${API}/marketplace-vendor/pos-bulk-pickup-tag?po_ids=${ids}`, { headers: authHeaders() })
      .then((r) => r.ok ? r.text() : Promise.reject(r))
      .then((html) => {
        const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
        const win = window.open(url, '_blank');
        if (!win) toast.error('Popup blocked — allow popups to print labels');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(() => toast.error('Could not open bulk pickup labels'));
  };

  // ── Payout helpers ────────────────────────────────────────────────────────
  const handleInvoiceFile = (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Invoice must be a PDF'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Invoice exceeds 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setInvoiceFile({ filename: file.name, base64: reader.result });
    reader.readAsDataURL(file);
  };

  const submitPayout = async () => {
    if (selectedPayoutPos.size === 0) { toast.error('Select at least one PO'); return; }
    if (!invoiceFile) { toast.error('Upload your consolidated invoice PDF first'); return; }
    setCreatingPayout(true);
    try {
      const create = await axios.post(`${API}/marketplace-vendor/payouts`, {
        po_ids: Array.from(selectedPayoutPos),
        invoice_base64: invoiceFile.base64,
        invoice_filename: invoiceFile.filename,
      }, { headers: authHeaders() });
      await axios.post(`${API}/marketplace-vendor/payouts/${create.data.payout_id}/submit`, {},
        { headers: authHeaders() });
      toast.success(`Payout request submitted · net ${create.data.net_amount}`);
      setSelectedPayoutPos(new Set());
      setInvoiceFile(null);
      await loadPayoutEligible();
    } catch (e) { toast.error(e.response?.data?.detail || 'Submission failed'); }
    finally { setCreatingPayout(false); }
  };

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { navigate('/marketplace-vendor/login'); return; }
    Promise.all([
      axios.get(`${API}/marketplace-vendor/me`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/marketplace-vendor/expiring-documents`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/marketplace-vendor/inventory`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/marketplace-vendor/announcements`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { rows: [], unread_count: 0 } })),
    ]).then(([m, d, inv, ann]) => {
      setMe(m.data); setDocs(d.data?.documents || {}); setInventory(inv.data); setAnnouncements(ann.data);
    }).catch((e) => {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem(TOKEN_KEY); navigate('/marketplace-vendor/login'); return;
      }
      toast.error(e.response?.data?.detail || 'Failed to load profile');
    }).finally(() => setLoading(false));
  }, [navigate]);

  // Lazy-load per-tab content
  useEffect(() => {
    if (activeNav === 'pos' && !pos) loadPOs();
    if (activeNav === 'performance' && !perf) loadPerf();
    if (activeNav === 'payouts' && !eligiblePos) loadPayoutEligible();
    if (activeNav === 'tickets' && !tickets) loadTickets();
    if (activeNav === 'announcements') loadAnnouncements();
    if (activeNav === 'dashboard' && !pos) loadPOs();
  }, [activeNav]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('marketplace_vendor_profile');
    navigate('/marketplace-vendor/login');
  };

  // ── Sidebar config ────────────────────────────────────────────────────────
  const sections = useMemo(() => ([
    {
      label: 'Main',
      items: [
        { id: 'dashboard',     label: 'Dashboard',       icon: LayoutGrid },
        { id: 'products',      label: 'My Products',     icon: Package },
        { id: 'pos',           label: 'Purchase Orders', icon: ShoppingBag },
        { id: 'performance',   label: 'Performance',     icon: BarChart3 },
        { id: 'payouts',       label: 'Payouts',         icon: Wallet },
      ],
    },
    {
      label: 'Others',
      items: [
        { id: 'tickets',       label: 'Support',         icon: MessageSquare,
          badge: tickets?.rows?.filter((t) => ['open', 'in_progress'].includes(t.status)).length || 0 },
        { id: 'announcements', label: 'Announcements',   icon: Megaphone, badge: announcements?.unread_count || 0 },
        { id: 'profile',       label: 'Profile',         icon: User },
      ],
    },
  ]), [tickets, announcements]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  if (!me) return null;

  const expiredOrWarning = Object.values(docs || {}).filter((d) => d.is_expired || d.is_warning);
  const highAnnouncement = (announcements?.rows || []).filter((a) => a.priority === 'high' && !a.is_read)[0];

  return (
    <div className="vp-root min-h-screen">
      <VendorSidebar
        vendor={me}
        sections={sections}
        activeId={activeNav}
        onChange={setActiveNav}
        onLogout={logout}
      />

      <div className="ml-0 md:ml-[260px] min-h-screen flex flex-col">
        <VendorPageHeader
          title={NAV_TITLES[activeNav]?.title || 'Dashboard'}
          subtitle={NAV_TITLES[activeNav]?.subtitle}
          notifyDot={(announcements?.unread_count || 0) > 0}
        />

        <main className="flex-1 px-6 lg:px-10 py-6 lg:py-8 max-w-[1600px] w-full mx-auto flex flex-col gap-6">
          {/* Compliance reminder banner */}
          {expiredOrWarning.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="mv-expiry-banner">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-semibold text-amber-900 mb-1">Document attention required</p>
                {Object.entries(docs).map(([key, d]) => {
                  if (!d.is_expired && !d.is_warning) return null;
                  const label = key === 'trade_license' ? 'Trade License' : 'Tax Certificate';
                  return (
                    <p key={key} className="text-amber-800">
                      {label}: <b className={d.is_expired ? 'text-rose-700' : 'text-amber-700'}>
                        {d.is_expired ? 'EXPIRED' : `${d.days_left} day${d.days_left === 1 ? '' : 's'} left`}
                      </b>{' '}(expires {d.expiry})
                    </p>
                  );
                })}
                <p className="text-xs text-amber-700 mt-1">Contact the company admin to upload renewed documents.</p>
              </div>
            </div>
          )}

          {/* High-priority announcement banner */}
          {highAnnouncement && (
            <div
              className="bg-gradient-to-r from-violet-50 to-rose-50 border border-violet-200 rounded-xl p-4 flex items-start gap-3"
              data-testid={`mv-announcement-banner-${highAnnouncement.id}`}
            >
              <Megaphone className="w-5 h-5 text-violet-600 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-semibold text-gray-900 mb-1">{highAnnouncement.title}</p>
                <p className="text-gray-700 whitespace-pre-wrap">{highAnnouncement.message}</p>
              </div>
              <button
                onClick={() => markAnnouncementRead(highAnnouncement.id)}
                className="text-xs text-gray-500 hover:text-gray-900"
                data-testid={`mv-announcement-dismiss-${highAnnouncement.id}`}
              >
                Mark read
              </button>
            </div>
          )}

          {activeNav === 'dashboard' && (
            <DashboardOverview me={me} inventory={inventory} pos={pos} announcements={announcements} onGo={setActiveNav} />
          )}
          {activeNav === 'products' && (
            <ProductsView
              inventory={inventory}
              editingSku={editingSku}
              editValues={editValues}
              setEditValues={setEditValues}
              beginEdit={beginEdit}
              cancelEdit={cancelEdit}
              saveEdit={saveEdit}
              savingEdit={savingEdit}
            />
          )}
          {activeNav === 'pos' && (
            <POsView pos={pos} poAction={poAction} openPickupTag={openPickupTag} downloadShippingLabel={downloadShippingLabel} bulkAction={bulkAction} bulkPrint={bulkPrint} />
          )}
          {activeNav === 'performance' && <PerformanceView perf={perf} />}
          {activeNav === 'payouts' && (
            <PayoutsView
              eligiblePos={eligiblePos}
              payouts={payouts}
              selectedPos={selectedPayoutPos}
              setSelectedPos={setSelectedPayoutPos}
              invoiceFile={invoiceFile}
              handleInvoiceFile={handleInvoiceFile}
              setInvoiceFile={setInvoiceFile}
              submitPayout={submitPayout}
              creatingPayout={creatingPayout}
            />
          )}
          {activeNav === 'tickets' && (
            <TicketsView
              tickets={tickets}
              onOpen={openTicket}
              onNew={() => setNewTicketOpen(true)}
              ticketDetail={ticketDetail}
              closeDetail={() => setTicketDetail(null)}
              onReply={async (id, message, attachments) => {
                try {
                  await axios.post(`${API}/marketplace-vendor/tickets/${id}/reply`,
                    { message, attachments: attachments || [] }, { headers: authHeaders() });
                  toast.success('Reply sent');
                  await openTicket(id); loadTickets();
                } catch (e) { toast.error(e.response?.data?.detail || 'Reply failed'); }
              }}
              newTicketOpen={newTicketOpen}
              closeNew={() => setNewTicketOpen(false)}
              onCreate={async (formPayload) => {
                const payload = {
                  subject: formPayload.subject, category: formPayload.category,
                  description: formPayload.description, attachments: formPayload.attachments || [],
                };
                if (formPayload.category === 'po_issue') {
                  payload.related_po_id = formPayload.related_po_id || null;
                  payload.related_po_number = formPayload.related_po_number || null;
                }
                try {
                  await axios.post(`${API}/marketplace-vendor/tickets`, payload, { headers: authHeaders() });
                  toast.success('Ticket created');
                  setNewTicketOpen(false); loadTickets();
                } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
              }}
            />
          )}
          {activeNav === 'announcements' && <AnnouncementsView announcements={announcements} onMarkRead={markAnnouncementRead} />}
          {activeNav === 'profile' && <ProfileView me={me} />}
        </main>
      </div>
    </div>
  );
};

const NAV_TITLES = {
  dashboard:     { title: 'Dashboard',       subtitle: 'Welcome back — here is your business at a glance' },
  products:      { title: 'My Products',     subtitle: 'Manage your assigned SKUs, cost and stock levels' },
  pos:           { title: 'Purchase Orders', subtitle: 'Accept, fulfil and track all your POs' },
  performance:   { title: 'Performance',     subtitle: 'Key fulfilment metrics over the last 90 days' },
  payouts:       { title: 'Payouts',         subtitle: 'Submit delivered POs for payment and track invoices' },
  tickets:       { title: 'Support',         subtitle: 'Raise and follow up on issues with our team' },
  announcements: { title: 'Announcements',   subtitle: 'Important updates from the marketplace admin' },
  profile:       { title: 'Profile',         subtitle: 'Your account details and onboarding info' },
};

export default MarketplaceVendorDashboardPage;

// ─────────────────────────────────────────────────────────────────────────────
//                           Dashboard Overview
// ─────────────────────────────────────────────────────────────────────────────

const DashboardOverview = ({ me, inventory, pos, announcements, onGo }) => {
  const totalProducts = inventory?.total || 0;
  const totalPOs = pos?.total || 0;
  const openPOs = (pos?.rows || []).filter((p) => ['pending_acceptance', 'accepted', 'ready_for_pickup'].includes(p.status)).length;
  const deliveredPOs = (pos?.rows || []).filter((p) => p.status === 'delivered').length;
  const ytdEarnings = (pos?.rows || [])
    .filter((p) => p.status === 'delivered')
    .reduce((s, p) => s + (p.subtotal_cost || 0), 0);
  const tatBreaches = (pos?.rows || []).filter((p) => p.tat_breached).length;

  const recentPOs = (pos?.rows || []).slice(0, 5);

  return (
    <>
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="mv-dashboard-kpi-row">
        <KpiCard label="Total POs" value={totalPOs} icon={ShoppingBag} testid="kpi-total-pos" />
        <KpiCard label="Open POs" value={openPOs} icon={Clock} testid="kpi-open-pos" />
        <KpiCard label="Delivered" value={deliveredPOs} icon={PackageCheck} testid="kpi-delivered" />
        <KpiCard label="Products" value={totalProducts} icon={Package} testid="kpi-products" />
        <KpiCard
          label="YTD earnings"
          value={fmtMoney(ytdEarnings, me?.currency || 'AED')}
          icon={Wallet}
          testid="kpi-earnings"
        />
      </div>

      {/* Quick status row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InfoTile
          icon={ShieldCheck} tone="emerald"
          label="Account status"
          value={(me.marketplace_status || '—').replace('_', ' ')}
        />
        <InfoTile
          icon={Clock} tone="amber"
          label="Fulfilment TAT"
          value={`${me.fulfillment_tat_days || 3} days`}
        />
        <InfoTile
          icon={Truck} tone="violet"
          label="Shipping mode"
          value={me.pickup_from_vendor ? 'Pickup from you' : (me.ships_to_warehouse ? 'You ship to warehouse' : 'Drop-ship to customer')}
        />
      </div>

      {tatBreaches > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-center gap-3 text-sm" data-testid="mv-tat-breach-banner">
          <AlertTriangle className="w-5 h-5 text-rose-500" />
          <span className="text-rose-900">
            <b>{tatBreaches}</b> purchase order{tatBreaches === 1 ? '' : 's'} have breached their fulfilment TAT.
          </span>
          <button onClick={() => onGo('pos')} className="ml-auto text-sm font-medium text-rose-700 hover:underline inline-flex items-center gap-1">
            View POs <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Recent POs */}
      <DataTableShell
        title="Recent Purchase Orders"
        actions={
          <button onClick={() => onGo('pos')} className="vp-btn text-xs" data-testid="mv-dashboard-view-all-pos">
            View all <ChevronRight className="w-3.5 h-3.5" />
          </button>
        }
        testid="mv-recent-pos-table"
      >
        {recentPOs.length === 0 ? (
          <EmptyState icon={ShoppingBag} title="No purchase orders yet" message="Customer orders for your SKUs will appear here once placed." />
        ) : (
          <table className="vp-table w-full">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Items</th>
                <th className="text-right">Earnings</th>
                <th>TAT due</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentPOs.map((p) => (
                <tr key={p.id} data-testid={`mv-dashboard-po-${p.id}`}>
                  <td className="font-mono text-[12px] text-gray-700">{p.order_number}</td>
                  <td className="text-gray-700">
                    {(p.line_items || []).slice(0, 2).map((li) => `${li.product_title || li.sku} ×${li.quantity}`).join(', ')}
                    {(p.line_items || []).length > 2 && <span className="text-gray-400"> +{p.line_items.length - 2} more</span>}
                  </td>
                  <td className="text-right font-semibold text-gray-900">{fmtMoney(p.subtotal_cost, p.currency)}</td>
                  <td className="text-gray-600 text-xs">{p.tat_due_at ? new Date(p.tat_due_at).toLocaleDateString() : '—'}</td>
                  <td><StatusPill status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTableShell>
    </>
  );
};

const InfoTile = ({ icon: Icon, label, value, tone = 'violet' }) => {
  const toneMap = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700' },
    violet: { bg: 'bg-violet-50', text: 'text-violet-700' },
  };
  const t = toneMap[tone] || toneMap.violet;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.04)]">
      <div className={`w-10 h-10 rounded-lg ${t.bg} ${t.text} flex items-center justify-center`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
        <div className="text-sm font-semibold text-gray-900 truncate capitalize">{value}</div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//                              Products View
// ─────────────────────────────────────────────────────────────────────────────

const ProductsView = ({ inventory, editingSku, editValues, setEditValues, beginEdit, cancelEdit, saveEdit, savingEdit }) => {
  const [search, setSearch] = useState('');
  const [stockFilter, setStockFilter] = useState('all'); // all | in_stock | out_of_stock
  if (!inventory) {
    return <DataTableShell testid="mv-assigned-inventory"><LoadingRow text="Loading products…" /></DataTableShell>;
  }
  const rows = (inventory.rows || []).filter((r) => {
    if (stockFilter === 'in_stock' && (r.quantity || 0) <= 0) return false;
    if (stockFilter === 'out_of_stock' && (r.quantity || 0) > 0) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.sku || '').toLowerCase().includes(q) || (r.product_title || '').toLowerCase().includes(q);
  });
  const inStockCount = (inventory.rows || []).filter((r) => (r.quantity || 0) > 0).length;
  const outOfStockCount = (inventory.rows || []).filter((r) => (r.quantity || 0) <= 0).length;

  return (
    <DataTableShell
      title={`My Products · ${inventory.total} assigned`}
      actions={
        <div className="inline-flex bg-gray-100 rounded-lg p-1 text-xs" data-testid="mv-inventory-stock-filter">
          {[
            { v: 'all', label: `All · ${inventory.rows?.length || 0}` },
            { v: 'in_stock', label: `In stock · ${inStockCount}` },
            { v: 'out_of_stock', label: `Out of stock · ${outOfStockCount}` },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setStockFilter(opt.v)}
              className={`px-2.5 py-1 rounded-md font-medium ${
                stockFilter === opt.v ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid={`mv-stock-filter-${opt.v}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      }
      search={
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SKU or product…"
            className="vp-input pl-9 w-full"
            data-testid="mv-inventory-search"
          />
        </div>
      }
      testid="mv-assigned-inventory"
    >
      {inventory.total === 0 ? (
        <EmptyState icon={Package} title="No products assigned" message="The admin will assign SKUs to your portal." />
      ) : (
        <table className="vp-table w-full" data-testid="mv-inventory-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th className="text-right">Cost</th>
              {inventory.can_see_commission && <th className="text-right">Commission</th>}
              {inventory.can_see_commission && <th className="text-right">Selling price</th>}
              <th className="text-right">Qty</th>
              <th className="text-right">Stock</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const editingCost = editingSku === `${row.sku}::cost`;
              const editingQty  = editingSku === `${row.sku}::quantity`;
              return (
                <tr key={row.sku}>
                  <td>
                    <div className="flex items-center gap-3">
                      {row.image_url ? (
                        <img src={row.image_url} alt="" className="w-9 h-9 rounded-lg object-cover bg-gray-100 shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 shrink-0">
                          <Package className="w-4 h-4" />
                        </div>
                      )}
                      <span className="truncate max-w-[260px] text-gray-900">{row.product_title || '—'}</span>
                    </div>
                  </td>
                  <td className="font-mono text-[12px] text-gray-500">{row.sku}</td>
                  <td className="text-right">
                    {editingCost && inventory.allow_edit_cost_price ? (
                      <div className="inline-flex items-center gap-1 justify-end">
                        <input
                          type="number" step="0.01" min="0"
                          value={editValues.cost_price}
                          onChange={(e) => setEditValues((s) => ({ ...s, cost_price: e.target.value }))}
                          className="vp-input w-24 text-right"
                          data-testid={`mv-edit-cost-${row.sku}`}
                        />
                        <button onClick={() => saveEdit(row, 'cost')} disabled={savingEdit}
                          className="p-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                          data-testid={`mv-save-cost-${row.sku}`}><Save className="w-3.5 h-3.5" /></button>
                        <button onClick={cancelEdit} disabled={savingEdit}
                          className="p-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50"
                          data-testid={`mv-cancel-cost-${row.sku}`}><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 justify-end">
                        <span className="text-gray-900 font-medium">{row.cost_price?.toFixed(2)}</span>
                        {inventory.allow_edit_cost_price && (
                          <button onClick={() => beginEdit(row, 'cost')}
                            className="p-1 rounded text-gray-400 hover:text-violet-700 hover:bg-violet-50"
                            data-testid={`mv-edit-cost-btn-${row.sku}`} title="Edit cost">
                            <Edit3 className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    )}
                  </td>
                  {inventory.can_see_commission && (
                    <td className="text-right text-gray-600">
                      {row.commission_pct}%{row.is_commission_override && <span className="ml-1 text-[10px] text-amber-600 font-medium">(override)</span>}
                    </td>
                  )}
                  {inventory.can_see_commission && (
                    <td className="text-right font-semibold text-violet-700" data-testid={`mv-selling-${row.sku}`}>
                      {row.selling_price}
                    </td>
                  )}
                  <td className="text-right">
                    {editingQty && inventory.allow_edit_quantity ? (
                      <div className="inline-flex items-center gap-1 justify-end">
                        <input
                          type="number" min="0" value={editValues.quantity}
                          onChange={(e) => setEditValues((s) => ({ ...s, quantity: e.target.value }))}
                          className="vp-input w-20 text-right"
                          data-testid={`mv-edit-qty-${row.sku}`}
                        />
                        <button onClick={() => saveEdit(row, 'quantity')} disabled={savingEdit}
                          className="p-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                          data-testid={`mv-save-qty-${row.sku}`}><Save className="w-3.5 h-3.5" /></button>
                        <button onClick={cancelEdit} disabled={savingEdit}
                          className="p-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50"
                          data-testid={`mv-cancel-qty-${row.sku}`}><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 justify-end">
                        <span className={(row.quantity || 0) === 0 ? 'text-rose-600 font-semibold' : 'text-gray-700'}>{row.quantity}</span>
                        {inventory.allow_edit_quantity && (
                          <button onClick={() => beginEdit(row, 'quantity')}
                            className="p-1 rounded text-gray-400 hover:text-violet-700 hover:bg-violet-50"
                            data-testid={`mv-edit-qty-btn-${row.sku}`} title="Edit quantity">
                            <Edit3 className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                      (row.quantity || 0) > 0
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-rose-50 text-rose-700'
                    }`}>
                      {(row.quantity || 0) > 0 ? 'In stock' : 'Out of stock'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </DataTableShell>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//                                 POs View
// ─────────────────────────────────────────────────────────────────────────────

const POsView = ({ pos, poAction, openPickupTag, downloadShippingLabel, bulkAction, bulkPrint }) => {
  const [selected, setSelected] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [drawer, setDrawer] = useState(null);

  if (!pos) {
    return <DataTableShell testid="mv-pos-list"><LoadingRow text="Loading purchase orders…" /></DataTableShell>;
  }

  // Feb 2026: counts per status so the tab bar shows live numbers.
  const counts = (pos.rows || []).reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});
  const TABS = [
    { v: '',                    label: 'All',         icon: ShoppingBag },
    { v: 'pending_acceptance',  label: 'New',         icon: Clock },
    { v: 'accepted',            label: 'Accepted',    icon: Check },
    { v: 'ready_for_pickup',    label: 'Ready',       icon: PackageCheck },
    { v: 'picked_up',           label: 'Picked up',   icon: Truck },
    { v: 'delivered',           label: 'Delivered',   icon: PackageCheck },
    { v: 'rejected',            label: 'Rejected',    icon: Ban },
    { v: 'cancelled',           label: 'Cancelled',   icon: X },
  ];

  const filtered = pos.rows.filter((p) => {
    if (statusFilter && p.status !== statusFilter) return false;
    if (search && !(p.order_number || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const toggleAll = (checked) => setSelected(checked ? new Set(filtered.map((p) => p.id)) : new Set());
  const selectedRows = filtered.filter((p) => selected.has(p.id));
  const canBulkAccept = selectedRows.length > 0 && selectedRows.every((p) => p.status === 'pending_acceptance');
  const canBulkReady  = selectedRows.length > 0 && selectedRows.every((p) => p.status === 'accepted');
  const canBulkPrint  = selectedRows.length > 0 && selectedRows.every(
    (p) => ['ready_for_pickup', 'picked_up', 'delivered'].includes(p.status) && p.shipping_mode !== 'drop_ship'
  );

  return (
    <div className="space-y-4">
      {/* Status tabs — Feb 2026 (replaces the old <select>) */}
      <div className="flex items-center gap-1 overflow-x-auto vp-scroll pb-1 -mx-1 px-1" data-testid="mv-po-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const n = t.v ? (counts[t.v] || 0) : (pos.rows?.length || 0);
          const active = statusFilter === t.v;
          return (
            <button
              key={t.v || 'all'}
              onClick={() => { setStatusFilter(t.v); setSelected(new Set()); }}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                active
                  ? 'bg-violet-600 text-white border-violet-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-violet-300 hover:text-violet-700'
              }`}
              data-testid={`mv-po-tab-${t.v || 'all'}`}
            >
              <Icon className="w-3 h-3" />
              {t.label}
              <span className={`ml-1 px-1.5 rounded text-[10px] ${active ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>{n}</span>
            </button>
          );
        })}
      </div>

      <DataTableShell
        title={`Order List · ${filtered.length} ${statusFilter ? '(filtered)' : 'total'}`}
        actions={null}
        search={
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search PO number…"
              className="vp-input pl-9 w-full"
              data-testid="mv-po-search"
            />
          </div>
        }
        testid="mv-pos-list"
      >
        {selected.size > 0 && (
          <div className="mx-6 mt-4 mb-2 bg-violet-50 border border-violet-200 rounded-lg px-4 py-2 flex flex-wrap items-center gap-2 text-xs" data-testid="mv-bulk-bar">
            <span className="text-violet-700 font-semibold">{selected.size} selected</span>
            <button
              disabled={!canBulkAccept}
              onClick={async () => { await bulkAction(Array.from(selected), 'bulk-accept'); setSelected(new Set()); }}
              className="vp-btn vp-btn-primary disabled:opacity-40 text-xs"
              data-testid="mv-bulk-accept"
            ><Check className="w-3 h-3" /> Accept all</button>
            <button
              disabled={!canBulkReady}
              onClick={async () => { await bulkAction(Array.from(selected), 'bulk-ready'); setSelected(new Set()); }}
              className="vp-btn disabled:opacity-40 text-xs"
              data-testid="mv-bulk-ready"
            ><PackageCheck className="w-3 h-3" /> Mark ready</button>
            <button
              disabled={!canBulkPrint}
              onClick={() => bulkPrint(Array.from(selected))}
              className="vp-btn disabled:opacity-40 text-xs"
              data-testid="mv-bulk-print"
            ><Printer className="w-3 h-3" /> Print labels</button>
            <button onClick={() => setSelected(new Set())} className="text-violet-600 hover:underline ml-auto">Clear</button>
          </div>
        )}
        {filtered.length === 0 ? (
          <EmptyState
            icon={ShoppingBag}
            title="No purchase orders match"
            message="Try adjusting your filters."
          />
        ) : (
          <table className="vp-table w-full" data-testid="mv-pos-table">
            <thead>
              <tr>
                <th className="w-8">
                  <input
                    type="checkbox"
                    checked={selected.size > 0 && selected.size === filtered.length}
                    onChange={(e) => toggleAll(e.target.checked)}
                    className="accent-violet-600 cursor-pointer"
                    data-testid="mv-po-select-all"
                  />
                </th>
                <th>Order #</th>
                <th>PO Date</th>
                <th>Items</th>
                <th className="text-right">Earnings</th>
                <th>Payment</th>
                <th>TAT due</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`mv-po-${p.id}`} className="cursor-pointer" onClick={() => setDrawer(p)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)}
                      className="accent-violet-600 cursor-pointer"
                      data-testid={`mv-po-select-${p.id}`}
                    />
                  </td>
                  <td className="font-mono text-[12px] text-gray-700">{p.order_number}</td>
                  <td className="text-gray-600 text-xs">
                    {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="text-gray-700 max-w-[320px]">
                    <div className="flex items-center gap-2">
                      {/* Product thumbnails (up to first 3 line items),
                          then text summary. Falls back to a Package
                          icon when no image. */}
                      <div className="flex -space-x-2 shrink-0">
                        {(p.line_items || []).slice(0, 3).map((li, idx) => (
                          li.image_url ? (
                            <img key={idx} src={li.image_url} alt=""
                              className="w-8 h-8 rounded-lg object-cover bg-gray-100 ring-2 ring-white" />
                          ) : (
                            <div key={idx} className="w-8 h-8 rounded-lg bg-gray-100 ring-2 ring-white flex items-center justify-center text-gray-400">
                              <Package className="w-3.5 h-3.5" />
                            </div>
                          )
                        ))}
                      </div>
                      <span className="truncate">
                        {(p.line_items || []).map((li) => `${li.product_title || li.sku} ×${li.quantity}`).join(', ')}
                      </span>
                    </div>
                  </td>
                  <td className="text-right font-semibold text-gray-900">{fmtMoney(p.subtotal_cost, p.currency)}</td>
                  <td>
                    <StatusPill
                      status={p.payment_status || 'unpaid'}
                      label={p.payment_status === 'paid' ? 'Paid' : p.payment_status === 'partially_paid' ? 'Partial' : 'Unpaid'}
                    />
                  </td>
                  <td className="text-gray-600 text-xs">
                    {p.tat_due_at ? new Date(p.tat_due_at).toLocaleDateString() : '—'}
                    {p.tat_breached && <span className="ml-1 text-rose-600 font-semibold">BREACHED</span>}
                  </td>
                  <td><StatusPill status={p.status} /></td>
                  <td className="text-right text-gray-400"><MoreHorizontal className="w-4 h-4 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTableShell>

      <DrawerDetail
        open={!!drawer}
        onClose={() => setDrawer(null)}
        title={drawer?.order_number}
        subtitle={drawer && <StatusPill status={drawer.status} />}
        width={520}
      >
        {drawer && (
          <div className="space-y-5">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Line items</div>
              <ul className="space-y-2">
                {(drawer.line_items || []).map((li, i) => (
                  <li key={i} className="flex justify-between items-center bg-gray-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-3 min-w-0">
                      {li.image ? (
                        <img src={li.image} alt={li.product_title || li.sku}
                          className="w-11 h-11 object-cover rounded-md border border-gray-200 flex-shrink-0"
                          data-testid={`mv-po-item-image-${li.sku}`}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : (
                        <div className="w-11 h-11 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                          <Package className="w-5 h-5 text-gray-300" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-sm text-gray-900 truncate">{li.product_title || li.sku}</div>
                        <div className="text-[11px] text-gray-500 font-mono">{li.sku}</div>
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-gray-900">×{li.quantity}</div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Earnings</div>
                <div className="text-base font-semibold text-violet-700">{fmtMoney(drawer.subtotal_cost, drawer.currency)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">TAT due</div>
                <div className="text-sm text-gray-900">{drawer.tat_due_at ? new Date(drawer.tat_due_at).toLocaleString() : '—'}</div>
              </div>
            </div>
            {drawer.awb_number && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
                Courier booked · AWB <b>{drawer.awb_number}</b> ({drawer.recommended_courier})
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
              {drawer.status === 'pending_acceptance' && (
                <>
                  <button onClick={() => { poAction(drawer.id, 'accept'); setDrawer(null); }}
                    className="vp-btn vp-btn-primary text-xs" data-testid={`mv-po-accept-${drawer.id}`}>
                    <Check className="w-3 h-3" /> Accept
                  </button>
                  <button onClick={() => {
                      const reason = window.prompt('Reason for rejection (required):', '');
                      if (reason && reason.trim()) { poAction(drawer.id, 'reject', { reason: reason.trim() }); setDrawer(null); }
                    }}
                    className="vp-btn text-rose-600 border-rose-200 hover:bg-rose-50 text-xs"
                    data-testid={`mv-po-reject-${drawer.id}`}>
                    <Ban className="w-3 h-3" /> Reject
                  </button>
                </>
              )}
              {drawer.status === 'accepted' && (
                <button onClick={() => { poAction(drawer.id, 'ready-for-pickup'); setDrawer(null); }}
                  className="vp-btn vp-btn-primary text-xs" data-testid={`mv-po-ready-${drawer.id}`}>
                  <PackageCheck className="w-3 h-3" /> Mark ready for pickup
                </button>
              )}
              {['ready_for_pickup', 'picked_up', 'delivered'].includes(drawer.status) && (
                drawer.shipping_mode === 'drop_ship' && drawer.label_url ? (
                  <a href={drawer.label_url} target="_blank" rel="noreferrer"
                    className="vp-btn text-xs" data-testid={`mv-po-courier-label-${drawer.id}`}>
                    <Printer className="w-3 h-3" /> Print courier label
                  </a>
                ) : drawer.shipping_mode === 'drop_ship' && !drawer.label_url ? (
                  <span className="text-xs text-amber-700">Courier label pending…</span>
                ) : (
                  <>
                    <button onClick={() => openPickupTag(drawer.id)} className="vp-btn text-xs" data-testid={`mv-po-print-${drawer.id}`}>
                      <Printer className="w-3 h-3" /> Print pickup tag
                    </button>
                    <button onClick={() => downloadShippingLabel(drawer.id)} className="vp-btn text-xs" data-testid={`mv-po-shipping-label-${drawer.id}`}>
                      <Printer className="w-3 h-3" /> Shipping label
                    </button>
                  </>
                )
              )}
            </div>
            {drawer.rejected_reason && (
              <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded p-2">
                <b>Rejected reason:</b> {drawer.rejected_reason}
              </div>
            )}
          </div>
        )}
      </DrawerDetail>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//                            Performance View
// ─────────────────────────────────────────────────────────────────────────────

const PerformanceView = ({ perf }) => {
  if (!perf) return <DataTableShell><LoadingRow text="Loading metrics…" /></DataTableShell>;
  const r = perf.rates || {};
  const t = perf.totals || {};
  const tile = (label, value, tone) => ({ label, value, tone });
  const headlines = [
    { label: 'Total POs',            value: perf.total_pos ?? 0,                          icon: ShoppingBag },
    { label: 'Delivered',            value: t.delivered ?? 0,                             icon: PackageCheck },
    { label: 'On-time pickups',      value: t.delayed != null ? `${(100 - (r.delay_rate_pct || 0)).toFixed(1)}%` : '—', icon: Clock },
    { label: 'Penalties (total)',    value: `${t.penalty_amount ?? 0}`,                   icon: AlertTriangle },
  ];
  const rates = [
    tile('Acceptance rate',   `${r.acceptance_rate_pct ?? 0}%`,    'emerald'),
    tile('Rejection rate',    `${r.rejection_rate_pct ?? 0}%`,     'amber'),
    tile('Cancellation rate', `${r.cancellation_rate_pct ?? 0}%`,  'gray'),
    tile('Delay rate',        `${r.delay_rate_pct ?? 0}%`,         'orange'),
    tile('Defect rate',       `${r.defect_rate_pct ?? 0}%`,        'rose'),
    tile('Return rate',       `${r.return_rate_pct ?? 0}%`,        'pink'),
  ];
  const toneClass = (t) => ({
    emerald: 'text-emerald-700', amber: 'text-amber-700', gray: 'text-gray-700',
    orange: 'text-orange-700', rose: 'text-rose-700', pink: 'text-pink-700',
  }[t] || 'text-gray-900');

  return (
    <div className="space-y-5" data-testid="mv-performance">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {headlines.map((h) => <KpiCard key={h.label} label={h.label} value={h.value} icon={h.icon} />)}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {rates.map((m) => (
          <div key={m.label} className="bg-white border border-gray-200 rounded-xl p-4 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.04)]">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{m.label}</div>
            <div className={`text-2xl font-bold mt-1 tracking-tight ${toneClass(m.tone)}`}>{m.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//                              Payouts View
// ─────────────────────────────────────────────────────────────────────────────

const PayoutsView = ({ eligiblePos, payouts, selectedPos, setSelectedPos, invoiceFile, handleInvoiceFile, setInvoiceFile, submitPayout, creatingPayout }) => {
  if (!eligiblePos) return <DataTableShell><LoadingRow text="Loading payouts…" /></DataTableShell>;

  const toggleSku = (id) => setSelectedPos((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const selectedRows = (eligiblePos.rows || []).filter((p) => selectedPos.has(p.id));
  const subtotal = selectedRows.reduce((s, p) => s + (p.subtotal_cost || 0), 0);
  const penalty = selectedRows.reduce((s, p) => s + (p.penalties || []).reduce((a, x) => a + (x.amount || 0), 0), 0);
  const net = subtotal - penalty;
  const currencies = new Set(selectedRows.map((p) => p.currency || 'AED'));
  const mixedCurrency = currencies.size > 1;
  const cur = selectedRows[0]?.currency || 'AED';

  const totalPaid = (payouts?.rows || []).filter((p) => p.status === 'paid').reduce((s, p) => s + (p.net_amount || 0), 0);
  const totalPending = (payouts?.rows || []).filter((p) => ['submitted', 'approved'].includes(p.status)).reduce((s, p) => s + (p.net_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="mv-payouts-tab">
      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard label="Eligible POs" value={eligiblePos.total} icon={PackageCheck} />
        <KpiCard label="Paid (lifetime)" value={fmtMoney(totalPaid, cur)} icon={Wallet} />
        <KpiCard label="Awaiting payment" value={fmtMoney(totalPending, cur)} icon={Clock} />
      </div>

      <DataTableShell
        title={`Eligible delivered POs · return window ${eligiblePos.return_window_days} days`}
        testid="mv-payout-eligible"
      >
        {eligiblePos.total === 0 ? (
          <EmptyState
            icon={Clock} title="No POs eligible yet"
            message={`Delivered POs become eligible after the ${eligiblePos.return_window_days}-day return window passes.`}
          />
        ) : (
          <>
            <table className="vp-table w-full" data-testid="mv-payout-eligible-table">
              <thead>
                <tr>
                  <th className="w-8"></th>
                  <th>Order #</th>
                  <th>Delivered</th>
                  <th className="text-right">Subtotal</th>
                  <th className="text-right">Penalty</th>
                  <th>Currency</th>
                </tr>
              </thead>
              <tbody>
                {eligiblePos.rows.map((p) => {
                  const pen = (p.penalties || []).reduce((a, x) => a + (x.amount || 0), 0);
                  return (
                    <tr key={p.id}>
                      <td>
                        <input type="checkbox" checked={selectedPos.has(p.id)} onChange={() => toggleSku(p.id)}
                          className="accent-violet-600 cursor-pointer"
                          data-testid={`mv-payout-check-${p.id}`} />
                      </td>
                      <td className="font-mono text-[12px] text-gray-700">{p.order_number}</td>
                      <td className="text-xs text-gray-600">{p.delivered_at ? new Date(p.delivered_at).toLocaleDateString() : '—'}</td>
                      <td className="text-right text-gray-900">{p.subtotal_cost}</td>
                      <td className="text-right text-rose-600">{pen || '—'}</td>
                      <td className="text-xs text-gray-600">{p.currency || 'AED'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Submission bar */}
            <div className="px-6 py-4 border-t border-gray-100 space-y-3 bg-gray-50">
              {mixedCurrency && (
                <div className="text-xs text-rose-600 font-medium" data-testid="mv-payout-currency-warn">
                  Mixed currencies selected — choose POs of a single currency.
                </div>
              )}
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="text-gray-500">Selected: <b className="text-gray-900">{selectedPos.size}</b></span>
                <span className="text-gray-500">Subtotal: <b className="text-gray-900" data-testid="mv-payout-subtotal">{subtotal.toFixed(2)}</b></span>
                <span className="text-gray-500">Penalty: <b className="text-rose-600" data-testid="mv-payout-penalty">{penalty.toFixed(2)}</b></span>
                <span className="text-gray-500 ml-auto text-base">Net: <b className="text-violet-700 text-lg" data-testid="mv-payout-net">{net.toFixed(2)}</b> {cur}</span>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="vp-btn text-xs cursor-pointer">
                  <Upload className="w-3.5 h-3.5" />
                  {invoiceFile ? `Replace · ${invoiceFile.filename}` : 'Upload invoice (PDF, max 5MB)'}
                  <input type="file" accept="application/pdf" className="hidden"
                    onChange={(e) => handleInvoiceFile(e.target.files?.[0])}
                    data-testid="mv-payout-upload-invoice" />
                </label>
                {invoiceFile && (
                  <button onClick={() => setInvoiceFile(null)} className="text-xs text-gray-500 hover:text-rose-600 inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                )}
                <button onClick={submitPayout}
                  disabled={creatingPayout || mixedCurrency || selectedPos.size === 0 || !invoiceFile}
                  className="ml-auto vp-btn vp-btn-primary disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold"
                  data-testid="mv-payout-submit">
                  {creatingPayout && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Submit payout request
                </button>
              </div>
            </div>
          </>
        )}
      </DataTableShell>

      <DataTableShell title="My payout requests" testid="mv-payout-history">
        {!payouts || payouts.total === 0 ? (
          <EmptyState icon={Wallet} title="No payout requests yet" message="Once you submit a payout request it will appear here." />
        ) : (
          <table className="vp-table w-full">
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Status</th>
                <th className="text-right">POs</th>
                <th className="text-right">Subtotal</th>
                <th className="text-right">Penalty</th>
                <th className="text-right">Adjustments</th>
                <th className="text-right">Net</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {payouts.rows.map((p) => {
                const adjSum = (p.adjustments || []).reduce((a, x) => a + (x.amount || 0), 0);
                return (
                  <tr key={p.id} data-testid={`mv-payout-row-${p.id}`}>
                    <td className="text-xs text-gray-600">{new Date(p.submitted_at || p.created_at).toLocaleDateString()}</td>
                    <td><StatusPill status={p.status} /></td>
                    <td className="text-right">{p.po_ids?.length}</td>
                    <td className="text-right">{p.subtotal_amount}</td>
                    <td className="text-right text-rose-600">{p.penalty_amount || '—'}</td>
                    <td className="text-right">{adjSum || '—'}</td>
                    <td className="text-right font-semibold text-violet-700">{p.net_amount} {p.currency}</td>
                    <td className="text-xs text-gray-600">
                      {p.status === 'paid' && p.bank_transfer_ref && (<>Ref: {p.bank_transfer_ref}</>)}
                      {p.status === 'rejected' && p.rejected_reason && (<>Reason: {p.rejected_reason}</>)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </DataTableShell>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//                              Tickets View
// ─────────────────────────────────────────────────────────────────────────────

const MAX_FILE_BYTES = 4.5 * 1024 * 1024;

const ingestFiles = async (files, setter) => {
  const arr = Array.from(files || []);
  const out = [];
  for (const f of arr) {
    if (f.size > MAX_FILE_BYTES) { toast.error(`${f.name} is over 4.5MB`); continue; }
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });
    out.push({ filename: f.name, content_type: f.type || 'application/octet-stream', data_base64: dataUrl, size: f.size });
  }
  if (out.length) setter((prev) => [...prev, ...out].slice(0, 5));
};

const TicketsView = ({ tickets, onOpen, onNew, ticketDetail, closeDetail, onReply, newTicketOpen, closeNew, onCreate }) => {
  const [replyMsg, setReplyMsg] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [form, setForm] = useState({
    subject: '', category: 'po_issue', description: '',
    related_po_id: '', related_po_number: '', attachments: [],
  });
  const [poQuery, setPoQuery] = useState('');
  const [poResults, setPoResults] = useState([]);
  const [poSearching, setPoSearching] = useState(false);
  const [poDropdownOpen, setPoDropdownOpen] = useState(false);

  useEffect(() => {
    if (form.category !== 'po_issue' || form.related_po_id) return;
    const token = localStorage.getItem(TOKEN_KEY);
    const handle = setTimeout(() => {
      setPoSearching(true);
      axios.get(`${API}/marketplace-vendor/po-search`, {
        params: { q: poQuery, limit: 15 }, headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => { setPoResults(r.data?.rows || []); setPoDropdownOpen(true); })
        .catch(() => setPoResults([]))
        .finally(() => setPoSearching(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [poQuery, form.category, form.related_po_id]);

  const pickPo = (po) => {
    setForm((f) => ({ ...f, related_po_id: po.id, related_po_number: po.order_number }));
    setPoDropdownOpen(false); setPoResults([]);
  };
  const clearPo = () => { setForm((f) => ({ ...f, related_po_id: '', related_po_number: '' })); setPoQuery(''); };

  const downloadAttachment = async (ticketId, messageId, att) => {
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const r = await axios.get(
        `${API}/marketplace-vendor/tickets/${ticketId}/attachments/${messageId}/${att.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const a = document.createElement('a');
      a.href = `data:${r.data.content_type};base64,${r.data.data_base64}`;
      a.download = r.data.filename || att.filename;
      a.click();
    } catch (e) { toast.error('Download failed'); }
  };

  if (!tickets) return <DataTableShell><LoadingRow text="Loading tickets…" /></DataTableShell>;

  return (
    <div data-testid="mv-tickets-tab">
      <DataTableShell
        title={`Support Tickets · ${tickets.total}`}
        actions={
          <button onClick={onNew} className="vp-btn vp-btn-primary text-xs" data-testid="mv-ticket-new">
            + New ticket
          </button>
        }
      >
        {tickets.total === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title="No support tickets yet"
            message="Raise one if you need help with a PO, payment, or inventory issue."
            action={<button onClick={onNew} className="vp-btn vp-btn-primary text-xs">+ New ticket</button>}
          />
        ) : (
          <table className="vp-table w-full">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Category</th>
                <th>Linked PO</th>
                <th>Messages</th>
                <th>Status</th>
                <th>Last activity</th>
              </tr>
            </thead>
            <tbody>
              {tickets.rows.map((t) => (
                <tr key={t.id} className="cursor-pointer" onClick={() => onOpen(t.id)} data-testid={`mv-ticket-${t.id}`}>
                  <td className="font-medium text-gray-900">{t.subject}</td>
                  <td className="text-gray-700">{CATS.find((c) => c.value === t.category)?.label || t.category}</td>
                  <td className="font-mono text-[12px] text-violet-700">{t.related_po_number || '—'}</td>
                  <td className="text-gray-600">{t.messages?.length || 0}</td>
                  <td><StatusPill status={t.status} /></td>
                  <td className="text-[12px] text-gray-500">{(t.last_activity_at || '').slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTableShell>

      {/* Create ticket drawer */}
      <DrawerDetail open={newTicketOpen} onClose={closeNew} title="New Support Ticket" width={520}>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-700 block mb-1">Subject</label>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="vp-input w-full" data-testid="mv-ticket-subject" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 block mb-1">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value, related_po_id: '', related_po_number: '' })}
              className="vp-input w-full"
              data-testid="mv-ticket-category"
            >
              {CATS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          {form.category === 'po_issue' && (
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1">
                Linked PO <span className="text-rose-500">*</span>
              </label>
              {form.related_po_id ? (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-violet-50 border border-violet-200">
                  <div className="text-sm">
                    <span className="font-mono text-violet-700">{form.related_po_number}</span>
                    <span className="text-[10px] text-gray-500 ml-2">linked</span>
                  </div>
                  <button type="button" onClick={clearPo} className="text-xs text-gray-500 hover:text-rose-600" data-testid="mv-ticket-po-clear">
                    Change
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input value={poQuery} onChange={(e) => setPoQuery(e.target.value)}
                    onFocus={() => setPoDropdownOpen(true)}
                    placeholder="Search by PO number…"
                    className="vp-input w-full font-mono"
                    data-testid="mv-ticket-po-search" />
                  {poDropdownOpen && (poResults.length > 0 || poSearching) && (
                    <div className="absolute z-10 mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto" data-testid="mv-ticket-po-results">
                      {poSearching && <div className="px-3 py-2 text-xs text-gray-500"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Searching…</div>}
                      {poResults.map((p) => (
                        <button type="button" key={p.id} onClick={() => pickPo(p)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                          data-testid={`mv-ticket-po-option-${p.id}`}>
                          <div className="font-mono text-sm text-violet-700">{p.order_number}</div>
                          <div className="text-[10px] text-gray-500">{p.status?.replace('_', ' ')} · {p.item_count} item(s) · {(p.created_at || '').slice(0, 10)}</div>
                        </button>
                      ))}
                      {!poSearching && poResults.length === 0 && <div className="px-3 py-2 text-xs text-gray-500">No matching PO</div>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-gray-700 block mb-1">Describe the issue</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={5} className="vp-input w-full" data-testid="mv-ticket-description" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 block mb-1">Attachments (optional, max 5 · 4.5MB each)</label>
            <input type="file" multiple
              onChange={(e) => ingestFiles(e.target.files, (upd) => setForm((f) => ({ ...f, attachments: typeof upd === 'function' ? upd(f.attachments) : upd })))}
              className="text-xs text-gray-700"
              data-testid="mv-ticket-attachments" />
            {form.attachments?.length > 0 && (
              <ul className="mt-2 space-y-1">
                {form.attachments.map((a, i) => (
                  <li key={i} className="flex items-center gap-2 text-[11px] text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-1">
                    <FileText className="w-3 h-3 text-violet-600" />
                    <span className="truncate flex-1">{a.filename}</span>
                    <span className="text-gray-500">{Math.round((a.size || 0) / 1024)}KB</span>
                    <button type="button"
                      onClick={() => setForm((f) => ({ ...f, attachments: f.attachments.filter((_, j) => j !== i) }))}
                      className="text-rose-500 hover:text-rose-700"
                      data-testid={`mv-ticket-attach-remove-${i}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button onClick={closeNew} className="vp-btn text-xs">Cancel</button>
            <button onClick={() => onCreate(form)}
              disabled={form.subject.length < 3 || form.description.length < 3 || (form.category === 'po_issue' && !form.related_po_id)}
              className="vp-btn vp-btn-primary text-xs disabled:opacity-50"
              data-testid="mv-ticket-submit">
              Create ticket
            </button>
          </div>
        </div>
      </DrawerDetail>

      {/* Ticket detail drawer */}
      <DrawerDetail
        open={!!ticketDetail} onClose={closeDetail}
        title={ticketDetail?.subject}
        subtitle={ticketDetail && (
          <span className="inline-flex items-center gap-2">
            <StatusPill status={ticketDetail.status} />
            <span className="text-xs text-gray-500">{CATS.find((c) => c.value === ticketDetail.category)?.label}</span>
            {ticketDetail.related_po_number && (
              <span className="text-xs text-violet-700 font-mono">PO {ticketDetail.related_po_number}</span>
            )}
          </span>
        )}
        width={560}
      >
        {ticketDetail && (
          <div className="space-y-3" data-testid="mv-ticket-detail">
            {(ticketDetail.messages || []).map((m) => (
              <div key={m.id}
                className={`p-3 rounded-lg ${m.author === 'vendor' ? 'bg-gray-100 ml-8' : 'bg-violet-50 mr-8 border border-violet-100'}`}>
                <div className="text-[10px] text-gray-500 mb-1 font-medium">
                  {m.author === 'vendor' ? 'You' : (m.author_name || 'Support')} · {(m.created_at || '').replace('T', ' ').slice(0, 16)}
                </div>
                <div className="text-sm text-gray-900 whitespace-pre-wrap">{m.message}</div>
                {m.attachments?.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {m.attachments.map((a) => (
                      <li key={a.id}>
                        <button type="button"
                          onClick={() => downloadAttachment(ticketDetail.id, m.id, a)}
                          className="flex items-center gap-2 text-[11px] text-violet-700 hover:underline"
                          data-testid={`mv-ticket-attach-dl-${a.id}`}>
                          <FileText className="w-3 h-3" />
                          <span>{a.filename}</span>
                          <span className="text-gray-500">{Math.round((a.size || 0) / 1024)}KB</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            {ticketDetail.status !== 'closed' && (
              <div className="space-y-2 pt-3 border-t border-gray-100">
                <textarea value={replyMsg} onChange={(e) => setReplyMsg(e.target.value)} rows={3}
                  placeholder="Write your reply…"
                  className="vp-input w-full"
                  data-testid="mv-ticket-reply-input" />
                <div className="flex items-center justify-between gap-2">
                  <input type="file" multiple onChange={(e) => ingestFiles(e.target.files, setReplyFiles)}
                    className="text-[10px] text-gray-600"
                    data-testid="mv-ticket-reply-attachments" />
                  <button onClick={() => { onReply(ticketDetail.id, replyMsg, replyFiles); setReplyMsg(''); setReplyFiles([]); }}
                    disabled={!replyMsg.trim()}
                    className="vp-btn vp-btn-primary text-xs disabled:opacity-50"
                    data-testid="mv-ticket-reply-submit">
                    Send reply
                  </button>
                </div>
                {replyFiles.length > 0 && (
                  <ul className="text-[11px] text-gray-700 space-y-1">
                    {replyFiles.map((a, i) => (
                      <li key={i} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded px-2 py-1">
                        <FileText className="w-3 h-3 text-violet-600" />
                        <span className="truncate flex-1">{a.filename}</span>
                        <button onClick={() => setReplyFiles((p) => p.filter((_, j) => j !== i))} className="text-rose-500 hover:text-rose-700">
                          <X className="w-3 h-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </DrawerDetail>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//                          Announcements View
// ─────────────────────────────────────────────────────────────────────────────

const AnnouncementsView = ({ announcements, onMarkRead }) => {
  const rows = announcements?.rows || [];
  if (rows.length === 0) {
    return (
      <DataTableShell>
        <EmptyState icon={Megaphone} title="No announcements" message="The marketplace admin will post updates here." />
      </DataTableShell>
    );
  }
  return (
    <div className="space-y-3 max-w-3xl" data-testid="mv-announcements-tab">
      {rows.map((a) => (
        <div key={a.id}
          className={`bg-white border rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.04)] ${a.is_read ? 'border-gray-200' : 'border-violet-300'}`}
          data-testid={`mv-announcement-${a.id}`}>
          <div className="flex items-center gap-2 mb-2">
            {!a.is_read && <span className="w-2 h-2 rounded-full bg-violet-500 shrink-0"></span>}
            <h4 className="text-base font-semibold text-gray-900">{a.title}</h4>
            {a.priority === 'high' && <StatusPill status="warning" label="High priority" className="ml-1" />}
            <span className="text-[11px] text-gray-500 ml-auto">{(a.created_at || '').slice(0, 10)}</span>
          </div>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{a.message}</p>
          {!a.is_read && (
            <button onClick={() => onMarkRead(a.id)} className="text-xs text-violet-700 hover:underline mt-3 font-medium" data-testid={`mv-announcement-read-${a.id}`}>
              Mark as read
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//                            Profile View
// ─────────────────────────────────────────────────────────────────────────────

const ProfileView = ({ me }) => {
  // `pickup_address` is sometimes a string (legacy onboarding) and
  // sometimes a structured object `{line1, city, country, ...}` (newer
  // onboarding flow). Coerce to a readable string so we never crash
  // React with an object-as-child error.
  const pickupAddr = (() => {
    const v = me.pickup_address;
    if (!v) return '—';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') {
      return [v.line1, v.line2, v.city, v.state, v.country, v.postal_code]
        .filter(Boolean).join(', ') || '—';
    }
    return String(v);
  })();

  // Bank details may be either an object on `me.bank_details` OR
  // legacy flat fields on `me` itself.
  const bank = me.bank_details || {};
  return (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl" data-testid="mv-profile-tab">
    <ProfileCard title="Company Info" rows={[
      { label: 'Vendor name', value: me.vendor_name || '—' },
      { label: 'Contact', value: me.contact_person || '—' },
      { label: 'Email', value: me.email || '—' },
      { label: 'Phone', value: me.phone || '—' },
      { label: 'Country / City', value: `${me.country || '—'}, ${me.city || '—'}` },
    ]} />
    <ProfileCard title="Compliance" rows={[
      { label: 'Trade license #', value: me.trade_license_number || '—' },
      { label: 'Expiry', value: me.trade_license_expiry || '—', icon: Calendar },
      { label: 'VAT #', value: me.vat_number || '—' },
      { label: 'VAT expiry', value: me.vat_expiry || '—', icon: Calendar },
      { label: 'Account status', value: (me.marketplace_status || '—').toUpperCase() },
      { label: 'Renew documents', node: <DocResubmitButton /> },
    ]} />
    <ProfileCard title="Logistics" rows={[
      { label: 'Pickup address', value: pickupAddr },
      { label: 'Fulfilment TAT', value: `${me.fulfillment_tat_days || 3} days` },
      { label: 'Shipping mode', value: me.pickup_from_vendor ? 'Pickup from you' : (me.ships_to_warehouse ? 'You ship to warehouse' : 'Drop-ship to customer') },
    ]} />
    <ProfileCard title="Bank" rows={[
      { label: 'Bank name', value: bank.bank_name || '—' },
      { label: 'Account holder', value: bank.account_holder || '—' },
      { label: 'Account #', value: bank.account_number || '—' },
      { label: 'IBAN / SWIFT', value: bank.iban || bank.swift || '—' },
    ]} />
  </div>
  );
};

const ProfileCard = ({ title, rows }) => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-[0_2px_8px_-2px_rgba(0,0,0,0.04)]">
    <div className="px-5 py-4 border-b border-gray-100">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    </div>
    <dl className="divide-y divide-gray-100">
      {rows.map((r, i) => (
        <div key={i} className="px-5 py-3 grid grid-cols-3 gap-3 items-baseline">
          <dt className="text-xs font-medium text-gray-500 col-span-1">{r.label}</dt>
          <dd className="text-sm text-gray-900 col-span-2 flex items-center gap-1.5">
            {r.icon && <r.icon className="w-3.5 h-3.5 text-gray-400" />}
            {r.node ? r.node : (
              typeof r.value === 'object' && r.value !== null
                ? JSON.stringify(r.value)
                : (r.value ?? '—')
            )}
          </dd>
        </div>
      ))}
    </dl>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
//                              Shared helpers
// ─────────────────────────────────────────────────────────────────────────────

const LoadingRow = ({ text }) => (
  <div className="p-8 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
    <Loader2 className="w-5 h-5 animate-spin" />{text}
  </div>
);

// ─── Vendor doc resubmit (trade license / VAT) — Feb 2026 ───
const DocResubmitButton = () => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [form, setForm] = useState({ doc_type: 'trade_license', number: '', expiry: '', file_base64: '', filename: '' });

  const loadSubs = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    axios.get(`${API}/marketplace-vendor/documents/submissions`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setSubmissions(r.data?.rows || []))
      .catch(() => {});
  };
  useEffect(() => { if (open) loadSubs(); }, [open]);

  const pickFile = (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Must be PDF'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, file_base64: reader.result, filename: file.name }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.number || !form.expiry || !form.file_base64) {
      toast.error('Fill all fields and attach the renewed PDF'); return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      await axios.post(`${API}/marketplace-vendor/documents/submit`, form,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Submitted for admin approval');
      setForm({ doc_type: 'trade_license', number: '', expiry: '', file_base64: '', filename: '' });
      loadSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const pending = submissions.filter((s) => s.status === 'pending').length;

  return (
    <>
      <button onClick={() => setOpen(true)} className="vp-btn vp-btn-primary text-xs" data-testid="mv-doc-resubmit-open">
        <Upload className="w-3 h-3" /> Renew / resubmit
        {pending > 0 && <span className="ml-1 px-1.5 rounded bg-white/20 text-[10px]">{pending} pending</span>}
      </button>
      <DrawerDetail
        open={open} onClose={() => setOpen(false)}
        title="Renew documents"
        subtitle="Upload your renewed copy — admin will approve before it replaces the current one"
        width={520}
      >
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-700 block mb-1">Document type</label>
            <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className="vp-input w-full" data-testid="mv-doc-type">
              <option value="trade_license">Trade license</option>
              <option value="vat_certificate">VAT certificate</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 block mb-1">New {form.doc_type === 'trade_license' ? 'license number' : 'VAT number'}</label>
            <input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="vp-input w-full" data-testid="mv-doc-number" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 block mb-1">New expiry date</label>
            <DateInput  value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} className="vp-input w-full" data-testid="mv-doc-expiry" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 block mb-1">Renewed copy (PDF, max 5MB)</label>
            <input type="file" accept="application/pdf" onChange={(e) => pickFile(e.target.files?.[0])} className="text-xs text-gray-700" data-testid="mv-doc-file" />
            {form.filename && <div className="text-[11px] text-gray-500 mt-1">{form.filename}</div>}
          </div>
          <div className="flex justify-end pt-2 border-t border-gray-100">
            <button onClick={submit} disabled={submitting} className="vp-btn vp-btn-primary text-xs disabled:opacity-50" data-testid="mv-doc-submit">
              {submitting && <Loader2 className="w-3 h-3 animate-spin" />} Submit for approval
            </button>
          </div>
          {submissions.length > 0 && (
            <div className="pt-3 border-t border-gray-100">
              <h4 className="text-xs font-semibold text-gray-700 mb-2">Previous submissions</h4>
              <ul className="space-y-1.5">
                {submissions.slice(0, 5).map((s) => (
                  <li key={s.id} className="flex items-center gap-2 text-[11px]" data-testid={`mv-doc-sub-${s.id}`}>
                    <StatusPill status={s.status} />
                    <span className="text-gray-700">{s.doc_type === 'trade_license' ? 'TL' : 'VAT'}</span>
                    <span className="font-mono text-gray-900">{s.number}</span>
                    <span className="text-gray-500 ml-auto">{(s.submitted_at || '').slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
              {submissions.find((s) => s.status === 'rejected' && s.rejection_reason) && (
                <div className="mt-2 text-[11px] text-rose-600 bg-rose-50 border border-rose-200 rounded p-2">
                  Last rejection: {submissions.find((s) => s.status === 'rejected').rejection_reason}
                </div>
              )}
            </div>
          )}
        </div>
      </DrawerDetail>
    </>
  );
};

